import type { OwnerListing } from "../../services/listings.service";
import { ACTIVATED_STATUS } from "../constants/host-hub-instant-book.consts";
import {
  DATE_BASED_PRICING_USE_TYPES,
  US_COUNTRY_CODE,
} from "../constants/host-hub-marketing-banners.consts";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

type MarketingBannerConfig = {
  type: string;
  /** Days after the host first sees the banner before it stops showing. */
  numExpirationDays?: number;
  /** Days a dismissal hides the banner for; omitted means dismissed for good. */
  numDismissalDays?: number;
};

type MarketingBannersConfigValue = {
  value?: unknown;
};

/** Per-host view/dismiss history for a banner, persisted in localStorage. */
type MarketingBannerImpression = {
  firstViewedAt: number;
  dismissedAt?: number;
};

type BannerVisibilityArgs = {
  config: MarketingBannerConfig;
  impression: MarketingBannerImpression | null;
  now: number;
};

const isOptionalDayCount = (candidate: unknown) =>
  candidate === undefined ||
  (typeof candidate === "number" &&
    Number.isFinite(candidate) &&
    candidate >= 0);

/**
 * Validates a config entry, including its optional day counts. An entry with
 * a malformed day count is rejected outright rather than treated as if the
 * field were omitted, since an omitted `numExpirationDays` means "never
 * expires".
 */
const isMarketingBannerConfig = (
  candidate: unknown,
): candidate is MarketingBannerConfig => {
  if (typeof candidate !== "object" || candidate === null) return false;
  const { numDismissalDays, numExpirationDays, type } = candidate as Record<
    string,
    unknown
  >;
  return (
    typeof type === "string" &&
    isOptionalDayCount(numExpirationDays) &&
    isOptionalDayCount(numDismissalDays)
  );
};

/**
 * Picks the entry with the given `type` out of the `host_hub_marketing_banners`
 * payload. Returns `undefined` when the config is missing or the entry is
 * malformed so callers can fail closed.
 */
const findMarketingBannerConfig = ({
  configValue,
  type,
}: {
  configValue: MarketingBannersConfigValue | undefined;
  type: string;
}): MarketingBannerConfig | undefined => {
  const banners = Array.isArray(configValue?.value) ? configValue.value : [];
  return banners
    .filter(isMarketingBannerConfig)
    .find((banner) => banner.type === type);
};

/**
 * Parses a stored impression, returning `null` for anything missing or
 * malformed so the banner is treated as never seen.
 */
const parseMarketingBannerImpression = (
  stored: string | null,
): MarketingBannerImpression | null => {
  if (!stored) return null;
  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as MarketingBannerImpression).firstViewedAt !== "number"
    ) {
      return null;
    }
    const { dismissedAt, firstViewedAt } = parsed as MarketingBannerImpression;
    return typeof dismissedAt === "number"
      ? { dismissedAt, firstViewedAt }
      : { firstViewedAt };
  } catch {
    return null;
  }
};

/**
 * True once `numExpirationDays` have passed since the host first saw the
 * banner. Never expires when the banner hasn't been seen or the config omits
 * `numExpirationDays`. Mirrors mobile's `isCalloutVisible`.
 */
const isMarketingBannerExpired = ({
  config,
  impression,
  now,
}: BannerVisibilityArgs): boolean =>
  impression !== null &&
  config.numExpirationDays != null &&
  now - impression.firstViewedAt >= config.numExpirationDays * DAY_IN_MS;

/**
 * True while a dismissal is in effect: for `numDismissalDays` after
 * dismissing, or for good when the config omits `numDismissalDays`. Mirrors
 * mobile's `isCalloutVisible`.
 */
const isMarketingBannerDismissed = ({
  config,
  impression,
  now,
}: BannerVisibilityArgs): boolean => {
  if (impression?.dismissedAt == null) return false;
  if (config.numDismissalDays == null) return true;
  return now - impression.dismissedAt < config.numDismissalDays * DAY_IN_MS;
};

/** Records the first view; later views leave the impression unchanged. */
const markMarketingBannerViewed = ({
  impression,
  now,
}: {
  impression: MarketingBannerImpression | null;
  now: number;
}): MarketingBannerImpression => impression ?? { firstViewedAt: now };

/** Records a dismissal, backfilling `firstViewedAt` if it was never set. */
const markMarketingBannerDismissed = ({
  impression,
  now,
}: {
  impression: MarketingBannerImpression | null;
  now: number;
}): MarketingBannerImpression => ({
  dismissedAt: now,
  firstViewedAt: impression?.firstViewedAt ?? now,
});

/** True when the host owns at least one active FSV listing in the US. */
const hasUsFsvListing = (listings: OwnerListing[] | undefined) =>
  (listings ?? []).some(
    (listing) =>
      listing.country === US_COUNTRY_CODE &&
      listing.full_service?.active === true,
  );

const isActiveDiyListing = (listing: OwnerListing) =>
  listing.status === ACTIVATED_STATUS && listing.full_service?.active !== true;

/**
 * True when the host owns at least one activated DIY listing, i.e. one that
 * isn't an active FSV listing. Mirrors mobile's
 * `selectCanShowDateBasedPricingCallout`.
 */
const hasDiyListing = (listings: OwnerListing[] | undefined) =>
  (listings ?? []).some(isActiveDiyListing);

type DateBasedPricingUseType = (typeof DATE_BASED_PRICING_USE_TYPES)[number];

const getUseTypePriority = (useType: string | undefined) =>
  DATE_BASED_PRICING_USE_TYPES.indexOf(useType as DateBasedPricingUseType);

/**
 * One activated DIY listing per space for the "Set date pricing" picker, preferring the
 * space's Event listing, then Production, then Off-Site. Mirrors mobile's
 * `getDateBasedPricingListings`.
 */
const getDateBasedPricingListings = (listings: OwnerListing[] | undefined) => {
  const listingBySpaceId = new Map<string, OwnerListing>();

  (listings ?? []).forEach((listing) => {
    const priority = getUseTypePriority(listing.use_type);
    if (!isActiveDiyListing(listing) || priority === -1) return;

    const spaceId = listing.parentSpaceId ?? listing._id;
    const currentListing = listingBySpaceId.get(spaceId);
    if (
      !currentListing ||
      priority < getUseTypePriority(currentListing.use_type)
    ) {
      listingBySpaceId.set(spaceId, listing);
    }
  });

  return Array.from(listingBySpaceId.values());
};

export {
  findMarketingBannerConfig,
  getDateBasedPricingListings,
  hasDiyListing,
  hasUsFsvListing,
  isMarketingBannerDismissed,
  isMarketingBannerExpired,
  type MarketingBannerConfig,
  type MarketingBannerImpression,
  type MarketingBannersConfigValue,
  markMarketingBannerDismissed,
  markMarketingBannerViewed,
  parseMarketingBannerImpression,
};
